import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, NgZone, AfterViewInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import {
  AnalyticsService,
  GA4_EVENTS,
  VENDOR_EVENTS,
} from 'src/app/shared/analytics';
import { analyticsDistinctId, rawIdFromUserInfo } from 'src/app/shared/analytics/identity';

declare var google: any;

type SignInStep = 'identifier' | 'otp';

@Component({
  selector: 'app-vendor-signin',
  templateUrl: './vendor-signin.component.html',
  styleUrl: './vendor-signin.component.scss',
})
export class VendorSigninComponent implements AfterViewInit, OnDestroy {
  currentStep: SignInStep = 'identifier';

  // Identifier (email or phone)
  identifier = '';
  identifierError = '';
  isLoading = false;

  // OTP
  otpCode = '';
  otpError = '';
  maskedIdentifier = '';
  otpChannel: 'email' | 'sms' = 'email';
  otpTimer = 0;
  private otpInterval: any;

  // Help desk
  showHelpModal = false;

  private readonly GOOGLE_CLIENT_ID = '46261176784-bq1i72v4laj1egabvm3r1mf5eq7kp9dn.apps.googleusercontent.com';
  private vendorBaseUri = environment.vendorBaseUri;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private http: HttpClient,
    private ngZone: NgZone,
    private analytics: AnalyticsService,
  ) {}

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      this.autoLogin(token);
    }
  }

  ngAfterViewInit(): void {
    this.initializeGoogleSignIn();
  }

  ngOnDestroy(): void {
    this.clearOtpTimer();
  }

  // ─── Helpers ───

  get isEmail(): boolean {
    return this.identifier.includes('@');
  }

  get isValidIdentifier(): boolean {
    const val = this.identifier.trim();
    if (!val) return false;
    if (val.includes('@')) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    }
    return /^[6-9]\d{9}$/.test(val.replace(/\D/g, ''));
  }

  get identifierPlaceholder(): string {
    return 'Enter email or phone number';
  }

  // ─── Google Sign-In ───

  private initializeGoogleSignIn(): void {
    if (typeof google === 'undefined') {
      setTimeout(() => this.initializeGoogleSignIn(), 500);
      return;
    }

    google.accounts.id.initialize({
      client_id: this.GOOGLE_CLIENT_ID,
      callback: (response: any) => this.handleGoogleCredential(response),
    });

    const btnEl = document.getElementById('google-login-btn');
    if (btnEl) {
      google.accounts.id.renderButton(btnEl, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: 380,
        logo_alignment: 'center',
      });
    }
  }

  private handleGoogleCredential(response: any): void {
    this.ngZone.run(() => {
      this.isLoading = true;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });

      this.http
        .post<any>(`${this.vendorBaseUri}/unauth/auth/google`, { token: response.credential }, { headers })
        .subscribe({
          next: (res: any) => {
            this.isLoading = false;
            if (res?.data?.token) {
              this.handleSuccessfulLogin(res);
            } else {
              this.snackBar.open('Google login failed. Please try again.', 'Close', { duration: 4000 });
            }
          },
          error: (err: any) => {
            this.isLoading = false;
            this.snackBar.open(err?.error?.error || 'Google login failed.', 'Close', { duration: 5000 });
          },
        });
    });
  }

  // ─── Step 1: Send OTP ───

  sendOtp(): void {
    const val = this.identifier.trim();
    if (!this.isValidIdentifier) {
      this.identifierError = val.includes('@')
        ? 'Please enter a valid email address.'
        : 'Please enter a valid 10-digit mobile number.';
      return;
    }

    this.isLoading = true;
    this.identifierError = '';

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
    const body = { identifier: val.includes('@') ? val : val.replace(/\D/g, '') };

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/send-login-otp`, body, { headers })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.maskedIdentifier = res?.masked || '';
          this.otpChannel = res?.channel || 'email';
          this.currentStep = 'otp';
          this.startOtpTimer();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.identifierError = err?.error?.error || 'Failed to send OTP. Please try again.';
        },
      });
  }

  // ─── Step 2: Verify OTP ───

  verifyOtp(): void {
    if (!this.otpCode || this.otpCode.length !== 6) {
      this.otpError = 'Please enter the 6-digit OTP.';
      return;
    }

    this.isLoading = true;
    this.otpError = '';

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
    const val = this.identifier.trim();
    const body = { identifier: val.includes('@') ? val : val.replace(/\D/g, ''), otp: this.otpCode };

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/verify-login-otp`, body, { headers })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res?.data?.token) {
            this.handleSuccessfulLogin(res);
          } else {
            this.otpError = 'Verification failed. Please try again.';
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          this.otpError = err?.error?.error || 'Invalid OTP. Please try again.';
        },
      });
  }

  // ─── Resend / Back ───

  resendOtp(): void {
    if (this.otpTimer > 0) return;
    this.otpCode = '';
    this.otpError = '';
    this.sendOtp();
  }

  goBackToIdentifier(): void {
    this.currentStep = 'identifier';
    this.otpCode = '';
    this.otpError = '';
    this.clearOtpTimer();
  }

  // ─── Login success ───

  private handleSuccessfulLogin(response: any): void {
    localStorage.setItem('accessToken', response.data.token);
    localStorage.setItem('tokenType', response.data.token_type);
    localStorage.setItem(
      'tokenExpiry',
      (Date.now() + response.data.expires_in * 1000 * 60).toString()
    );
    localStorage.setItem('profilePicture', response.data.profile_picture);

    const userInfo = {
      sub: response.data.id || response.data.sub || '',
      'custom:team': response.data.contributor_code,
      'custom:visibility': 'vendor',
      email: response.data.email,
      name: response.data.name || '',
      phone_number: response.data.phone || '',
      phone_number_verified: false,
      token_use: 'id',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 60 * 60,
      jti: response.data.jti || '',
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo));

    const distinctId = analyticsDistinctId('vendor', rawIdFromUserInfo('vendor', userInfo));
    this.analytics.identify({
      distinctId: distinctId ?? '',
      role: 'vendor',
      properties: {
        email: userInfo.email,
        name: userInfo.name,
        phone: userInfo.phone_number,
        contributor_code: userInfo['custom:team'],
        contributor_id: String(userInfo.sub),
      },
    });
    this.analytics.track(GA4_EVENTS.LOGIN, { method: 'otp', role: 'vendor' });
    this.analytics.track(VENDOR_EVENTS.VENDOR_LOGGED_IN, { method: 'otp' });

    // Resolve the post-login landing page. Precedence:
    //   1. pendingBidProject  — user was mid-flow on an open-bid
    //   2. redirectUrl saved by AuthGuard, but ONLY if it's a vendor route
    //      (defensive: avoids sending a vendor into /workspace/sales-console
    //      etc. if some non-vendor URL was captured before login)
    //   3. default vendor landing page
    const pendingProject = localStorage.getItem('pendingBidProject');
    const savedRedirect = localStorage.getItem('redirectUrl');
    let redirectUrl: string;
    if (pendingProject) {
      redirectUrl = '/workspace/vendor/briefs';
    } else if (savedRedirect && savedRedirect.startsWith('/workspace/vendor')) {
      redirectUrl = savedRedirect;
    } else {
      redirectUrl = '/workspace/vendor/profile';
    }
    localStorage.removeItem('pendingBidProject');
    localStorage.removeItem('redirectUrl');

    this.router.navigateByUrl(redirectUrl).then(() => {
      this.snackBar.open('Welcome back!', 'Close', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['success-snackbar'],
      });
    });
  }

  // ─── Auto-login via token param ───

  autoLogin(token: string): void {
    const url = environment.apiUrl + 'auto-login?token=' + token;
    this.http.post<any>(url, {}).subscribe(
      (response: any) => {
        if (response?.data?.token) {
          this.handleSuccessfulLogin(response);
        } else {
          this.snackBar.open('Login failed', 'Close', { duration: 3000 });
        }
      },
      () => {
        this.snackBar.open('Error during auto-login', 'Close', { duration: 3000 });
      }
    );
  }

  // ─── OTP Input formatting ───

  formatOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
    this.otpCode = input.value;
  }

  // ─── Timer ───

  private startOtpTimer(): void {
    this.clearOtpTimer();
    this.otpTimer = 60;
    this.otpInterval = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) this.clearOtpTimer();
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
    this.otpTimer = 0;
  }
}
